import React from 'react';
import { Alert, AlertTitle, Button, Skeleton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUnifiedComplianceAlerts } from '../hooks/useUnifiedComplianceAlerts';
import { Upload, Visibility } from '@mui/icons-material';

export const ComplianceAlertBanner: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { 
        loading, 
        missingCount, 
        expiredCount, 
        expiringSoonCount,
        pendingCount,
    } = useUnifiedComplianceAlerts();

    if (loading) {
        return <Skeleton variant="rounded" height={60} sx={{ mb: 3 }} />;
    }

    const hasUrgentIssues = missingCount > 0 || expiredCount > 0;
    const hasWarnings = expiringSoonCount > 0;
    const hasPending = pendingCount > 0;

    if (!hasUrgentIssues && !hasWarnings && !hasPending) {
        return null; // All good!
    }

    let severity: 'error' | 'warning' | 'info' = 'warning';
    let title = 'Document Compliance Action Required';
    let message = '';
    let buttonLabel = 'Update Documents';

    if (hasUrgentIssues) {
        severity = 'error';
        const parts = [];
        if (missingCount > 0) parts.push(`${missingCount} missing document${missingCount > 1 ? 's' : ''}`);
        if (expiredCount > 0) parts.push(`${expiredCount} expired document${expiredCount > 1 ? 's' : ''}`);
        message = `You have ${parts.join(' and ')} that must be uploaded immediately to maintain compliance.`;
        if (hasWarnings) {
            message += ` Additionally, ${expiringSoonCount} document${expiringSoonCount > 1 ? 's are' : ' is'} expiring within 30 days.`;
        }
        if (hasPending) {
            message += ` Note: ${pendingCount} document${pendingCount > 1 ? 's are' : ' is'} currently under admin review.`;
        }
    } else if (hasWarnings) {
        severity = 'warning';
        message = `You have ${expiringSoonCount} document${expiringSoonCount > 1 ? 's' : ''} expiring within the next 30 days. Please update them soon.`;
        if (hasPending) {
            message += ` Note: ${pendingCount} document${pendingCount > 1 ? 's are' : ' is'} currently under admin review.`;
        }
    } else if (hasPending) {
        severity = 'info';
        title = 'Documents Under Review';
        message = `Your trade license document${pendingCount > 1 ? 's have' : ' has'} been uploaded and ${pendingCount > 1 ? 'are' : 'is'} currently under review by our compliance team. Job receipt will automatically activate upon approval.`;
        buttonLabel = 'View Documents';
    }

    return (
        <Alert 
            severity={severity} 
            sx={{ mb: 3, alignItems: 'center' }}
            action={
                location.pathname !== '/documents' ? (
                    <Button 
                        color="inherit" 
                        size="small" 
                        variant="outlined" 
                        startIcon={severity === 'info' ? <Visibility /> : <Upload />}
                        onClick={() => navigate('/documents')}
                    >
                        {buttonLabel}
                    </Button>
                ) : undefined
            }
        >
            <AlertTitle sx={{ mb: 0.5, fontWeight: 'bold' }}>{title}</AlertTitle>
            {message}
        </Alert>
    );
};
